<div class="flex items-start">
  @if (selectable()) {
    <div class="flex items-center p-0.5">
      <mat-checkbox
        [checked]="selected()"
        [style.visibility]="selectionEnabled() ? 'visible' : 'hidden'"
        (change)="toggleSelection()"
      ></mat-checkbox>
    </div>
  }
  <div class="flex w-full flex-col">
    <shared-list-item-expanded-template
      #prescriptionElement
      class="w-full"
      [configurableClasses]="{
        'bg-success-100': highlighted(),
        'border-2 border-success-500': highlighted() && !mappingInvalid() && !trademarksIdentifierMismatch(),
        'border-2 border-warning-500': mappingInvalid() || trademarksIdentifierMismatch(),
      }"
    >
      <ng-container header-indicators>
        <prescriptions-icon [prescriptionType]="prescription().type"></prescriptions-icon>
        @if (prescription().isFreeForm) {
          <shared-info-badge
            class="text-nowrap"
            [color]="colors.Orange"
            [withBorder]="true"
            [fontSize]="'text-xs'"
            [text]="'prescription.label.freeForm' | translate"
          ></shared-info-badge>
        }
        @if (hasActionIndicator()) {
          <span
            class="flex items-center gap-1 rounded px-2 py-1 text-xs font-medium"
            [class.bg-green-100]="isCompleted()"
            [class.text-green-800]="isCompleted()"
            [class.bg-blue-100]="isPlanned()"
            [class.text-blue-800]="isPlanned()"
          >
            <mat-icon class="h-4 w-4 text-base">
              {{ isCompleted() ? "check_circle" : "schedule" }}
            </mat-icon>
            {{ isCompleted() ? ("prescription.label.completed" | translate) : ("prescription.label.planned" | translate) }}
          </span>
        }
        @if (prescription().creationDate) {
          <div
            class="flex items-center gap-1 text-sm"
            [matTooltip]="'prescription.descriptions.creationDate' | translate"
          >
            <mat-icon>event</mat-icon>
            <span>{{ formatDate(prescription().lastUpdateDate, true) }}</span>
            <span>({{ prescription().creatorName }})</span>
          </div>
          <div
            class="flex items-center gap-1 text-sm"
            [matTooltip]="'prescription.descriptions.lastChangeDate' | translate"
          >
            <mat-icon>edit_calendar</mat-icon>
            <span>{{ formatDate(prescription().lastUpdateDate, true) }}</span>
            <span>({{ prescription().lastUpdateByName }})</span>
          </div>
        }
        @if (
          (prescription().count && prescription().count > 1) ||
          (prescription().metadata["quantity"] && prescription().metadata["quantity"] > 1) ||
          (prescription().metadata["treatment_dose"] && prescription().metadata["treatment_dose"] > 1)
        ) {
          <div
            class="flex items-center gap-1 text-sm"
            [matTooltip]="'prescription.descriptions.quantity' | translate: { used: prescription().used, declined: prescription().declined }"
          >
            <mat-icon>done_all</mat-icon>
            @switch (prescription().type) {
              @case (prescriptionTypes.Drugs) {
                @if (prescription().metadata["treatment_dose"]) {
                  {{ prescription().metadata["treatment_dose"] + " од." }}
                }
                @if (prescription().metadata["quantity"]) {
                  {{ prescription().metadata["quantity"] + " од." }}
                }
              }
              @default {
                @if (prescription().count && prescription().used !== null && prescription().declined !== null) {
                  {{ prescription().used + prescription().declined }} / {{ prescription().count }}
                } @else if (prescription().metadata["quantity"]) {
                  {{ prescription().metadata["quantity"] }}
                }
              }
            }
          </div>
        }
      </ng-container>
      <ng-container header-actions>
        <shared-list-item-neutral-button (click)="toggleSimilarPrescriptions()">
          @if (!similarChecked()) {
            {{ "prescription.action.similarPrescriptions" | translate }}
          } @else if (hasSimilar()) {
            {{ "prescription.action.alreadyPrescribed" | translate }}
          } @else {
            {{ "prescription.action.notPrescribed" | translate }}
          }
          <mat-icon [class.text-warning-600]="hasSimilar()">chronic</mat-icon>
        </shared-list-item-neutral-button>
        @if (historicalChangesExist()) {
          <shared-list-item-neutral-button (click)="toggleHistoricalInfo()">
            {{ "buttons.prescription.history" | translate }}
            <mat-icon>history</mat-icon>
          </shared-list-item-neutral-button>
        }
        @if (showAppointmentsDetails()) {
          <shared-list-item-neutral-button (click)="openAppointmentsDetailsDialog()">
            {{ "buttons.prescription.appointmentsDetails" | translate }}
            <mat-icon>event</mat-icon>
          </shared-list-item-neutral-button>
        }
        @if (showPrescriptionActions()) {
          <shared-list-item-neutral-button [matMenuTriggerFor]="prescriptionMenu">
            {{ "buttons.prescription.actionsList" | translate }}
            <mat-icon>more_vert</mat-icon>
          </shared-list-item-neutral-button>
          <mat-menu #prescriptionMenu="matMenu">
            @if (isEditEnabled()) {
              <button
                mat-menu-item
                class="text-lg"
                (click)="openManagerModal()"
              >
                <mat-icon>edit</mat-icon>
                {{ "prescription.action.edit" | translate }}
              </button>
              @if (!this.prescription().guaranteeApplicationId) {
                <button
                  mat-menu-item
                  class="text-error-600"
                  (click)="deleteDraftPrescription(prescription().id)"
                >
                  <mat-icon class="text-error-600">delete</mat-icon>
                  {{ "prescription.action.deleteDraft" | translate }}
                </button>
              }
            }
            @if (showCreateGuaranteeFlow()) {
              <button
                mat-menu-item
                (click)="openCreateGuaranteeFlow()"
              >
                <mat-icon>event_available</mat-icon>
                {{ createGuaranteeFlowLabel() | translate }}
                <span class="ml-2 rounded-full bg-purple-100 px-2 py-0.5 text-xs font-semibold text-purple-700">{{ "general.label.beta" | translate }}</span>
              </button>
            }
            @if (showManualAppointmentCreation()) {
              <button
                mat-menu-item
                (click)="openModalByPrescriptionType(prescription().type)"
              >
                <mat-icon>schedule</mat-icon>
                {{ "prescription.action.createAppointment" | translate }}
              </button>
            }
            <!-- TODO: DRAFT / TEST-ONLY — pharmacy ordering for drugs; gated by DRUG_PHARMACY_ORDER_ENABLED -->
            @if (showPharmacyOrderCreation()) {
              <button
                mat-menu-item
                (click)="openPharmacyOrderModal()"
              >
                <mat-icon>local_pharmacy</mat-icon>
                {{ "pharmacyOrder.action.menuOption" | translate }}
                <span class="ml-2 rounded-full bg-purple-100 px-2 py-0.5 text-xs font-semibold text-purple-700">{{ "general.label.beta" | translate }}</span>
              </button>
            }
            @if (showAppointmentBinding()) {
              <button
                mat-menu-item
                [disabled]="isBindingDisabled()"
                (click)="bindToExistingAppointment()"
              >
                <mat-icon>link</mat-icon>
                {{ "prescription.action.bindAppointment" | translate }}
              </button>
            }
            @if (showCompleteAppointment()) {
              <button
                mat-menu-item
                (click)="completeAppointment()"
                [matTooltip]="'prescription.action.completeAppointment' | translate"
              >
                <mat-icon>check</mat-icon>
                {{ "prescription.action.completeAppointment" | translate }}
              </button>
            }
            @if (showCompleteLaboratoryOrder()) {
              <button
                mat-menu-item
                (click)="completeLaboratoryOrder()"
                [matTooltip]="'prescription.action.completeLaboratoryOrder' | translate"
              >
                <mat-icon>check</mat-icon>
                {{ "prescription.action.completeLaboratoryOrder" | translate }}
              </button>
            }
            @if (showAppointmentCancel()) {
              @if (prescription().type === prescriptionTypes.LaboratoryService) {
                <button
                  mat-menu-item
                  (click)="cancelLaboratoryOrder()"
                >
                  <mat-icon>cancel</mat-icon>
                  {{ "prescription.action.cancelLaboratoryOrder" | translate }}
                </button>
              } @else {
                <button
                  mat-menu-item
                  (click)="cancelAppointment()"
                >
                  <mat-icon>cancel</mat-icon>
                  {{ "prescription.action.cancelAppointment" | translate }}
                </button>
              }
            }
            @if (showPrescriptionDelete()) {
              <button
                mat-menu-item
                class="text-error-600"
                (click)="deletePrescription()"
              >
                <mat-icon class="text-error-600">delete</mat-icon>
                {{ "prescription.action.delete" | translate }}
              </button>
            }
          </mat-menu>
        }
      </ng-container>
      <div class="relative col-span-5 flex flex-col gap-2 text-lg">
        <div class="flex flex-col items-start gap-1">
          @if (prescription().type === prescriptionTypes.Drugs) {
            <p class="subheader-2">{{ medicationFullName() }}</p>
          } @else {
            @if (doctorName()) {
              <p class="subheader-2">{{ doctorName() }}</p>
            } @else {
              <p class="subheader-2">
                {{ prescription().name }}
              </p>
            }
          }
        </div>
        <div class="flex items-center gap-2 pb-3">
          @if (prescription().franchise || prescription().franchise === 0) {
            <shared-info-badge
              class="text-nowrap"
              [color]="colors.Blue"
              [withBorder]="true"
              [fontSize]="'text-xs'"
              [text]="'prescription.label.franchise' | translate: { percentage: prescription().franchise }"
            ></shared-info-badge>
            <mat-icon
              class="text-info-600"
              [matTooltip]="getFranchiseLabel(prescription().franchise)"
              >info_outline</mat-icon
            >
          }
          @if (prescription().type === prescriptionTypes.Drugs) {
            <shared-pharmacies-count
              [pharmaciesCount]="pharmaciesCount()"
              [pharmaciesLoading]="loadingPharmaciesCount()"
              (click)="getPharmaciesCount(prescription())"
            ></shared-pharmacies-count>
          }
          @if (showLimitsInfo()) {
            <shared-limit
              [limits]="limits()"
              [loadingLimits]="loadingLimits()"
              (click)="getLimitsRules()"
            ></shared-limit>
          }
          @if (prescription().metadata["code"] && prescription().metadata["code"] === noAchiCode) {
            <shared-warning-message
              [text]="'warning.service.noAchi' | translate"
              [withBorder]="true"
              [size]="'small'"
            ></shared-warning-message>
          }
          @if (mappingInvalid()) {
            <shared-warning-message
              [text]="'prescription.message.invalidMappingWarning' | translate"
              [withBorder]="true"
              [size]="'small'"
            ></shared-warning-message>
          }
          @if (trademarksIdentifierMismatch()) {
            <shared-warning-message
              [text]="'prescription.message.trademarksIdentifierMismatch' | translate"
              [withBorder]="true"
              [size]="'small'"
            ></shared-warning-message>
          }
        </div>
      </div>
      <div class="col-span-3 flex min-w-[270px] flex-col">
        <prescriptions-period
          [startDate]="prescription().startDate"
          [endDate]="prescription().endDate"
        ></prescriptions-period>
        @if (actualPrescriptionPlanning()) {
          <div
            class="flex items-center justify-end gap-1 py-1 text-sm"
            [matTooltip]="'prescription.descriptions.plannedAppointmentDate' | translate"
          >
            <mat-icon>event_available</mat-icon>
            <span>{{ formatDate(actualPrescriptionPlanning()?.dateStart, true) }} - {{ formatDate(actualPrescriptionPlanning()?.dateEnd, true) }}</span>
          </div>
        }
      </div>
      <div class="col-span-4 flex place-content-end">
        <div class="flex flex-col gap-2">
          <div class="flex flex-col items-start justify-center gap-2">
            <prescriptions-status [prescription]="prescription()"></prescriptions-status>
            @if (showPrescriptionActions() && prescription().guaranteeStatus === guaranteeStatus.Unknown) {
              <div class="flex gap-2">
                <button
                  class="btn btn-primary px-5 py-1 text-xs font-thin"
                  [type]="'button'"
                  (click)="handlePrescription(true)"
                >
                  Гарантувати
                </button>
                <button
                  class="btn btn-secondary border-error-600 px-5 py-1 text-xs text-error-600"
                  [type]="'button'"
                  (click)="handlePrescription(false)"
                >
                  Відмовити
                </button>
              </div>
            }
          </div>
        </div>
      </div>
      @if (itemLoading()) {
        <div class="absolute z-50 flex h-24 w-32 items-center justify-center justify-self-center rounded-2xl bg-neutral-100">
          <shared-spin-loading [size]="24"></shared-spin-loading>
        </div>
      }
    </shared-list-item-expanded-template>

    @if (historicalChangesExist() && showHistoricalChanges()) {
      <div class="relative z-0 mx-4 flex flex-col gap-2 rounded-b-2xl border-t-0 bg-neutral-100 p-2 shadow-md hover:cursor-pointer">
        <prescriptions-changes-list [prescriptionChanges]="prescription().historicalChanges" />
      </div>
    }

    @if (showSimilarPrescriptions()) {
      <prescriptions-similar-prescriptions
        [prescriptions]="similarPrescriptions()"
        [loading]="similarLoading()"
      />
    }
  </div>
</div>
